import React, { useState, useRef } from 'react';
import { ArrowDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import InteractivePricingCalculator from './InteractivePricingCalculator';

const PricingContainer = styled.div`
  background-color: #0a192f;
  color: #ffffff;
  padding: clamp(2rem, 5vw, 5rem);
  margin-top: -3rem;
`;

const Title = styled.h1`
  font-family: "Raleway-Regular";
  color: #e1ad21;
  text-align: center;
  font-size: clamp(2rem, 4vw, 3rem);

  @media (max-width: 768px) {
    margin-top: 5rem;
    margin-bottom: 2.5rem;
  }

  @media (min-width: 1920px) {
    margin-top: 3rem;
  }
    
  @media (min-width: 2560px) {
    margin-top: 4rem;
    margin-bottom: 2.5rem;
  }
`;

const Description = styled.p`
  font-family: "Helvetica";
  text-align: center;
   margin: 0 auto 2rem auto;
  font-size: clamp(1rem, 2vw, 1.2rem);
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.75;

  @media (max-width: 768px) {
    line-height: 1.85;
    margin: 0 auto 2.5rem auto;
  }

  @media (min-width: 1920px) {
    line-height: 1.65;
  }

`;

const TabContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 0rem;
  flex-wrap: wrap;
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const TabButton = styled.button`
  background-color: ${props => props.active ? '#e1ad21' : 'transparent'};
  color: ${props => props.active ? '#0a192f' : '#ffffff'};
  border: 2px solid #e1ad21;
  padding: 0.5rem 1rem;
  margin: 0 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: "Helvetica";
  font-size: clamp(0.8rem, 1.5vw, 1rem);
  margin-bottom: 1rem;

  &:hover {
    background-color: #e1ad21;
    color: #0a192f;
  }

  @media (max-width: 768px) {
    margin: 0.5rem 0;
  }
`;

const PricingGrid = styled(motion.div)`
  display: grid;
  font-family: "Helvetica";
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: clamp(1rem, 2vw, 2rem);
  margin-bottom: 3rem;
  justify-content: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    max-width: 300px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const RetainerGrid = styled(motion.div)`
  display: grid;
  font-family: "Helvetica";
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: clamp(1rem, 2vw, 2rem);
  margin-bottom: 3rem;
  justify-content: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    max-width: 350px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const CapacityNote = styled.div`
  background: rgba(225, 173, 33, 0.1);
  border-left: 3px solid #e1ad21;
  padding: 1rem 1.5rem;
  border-radius: 6px;
  margin-bottom: 2rem;
  font-family: "Helvetica";
  font-size: clamp(0.9rem, 1.3vw, 1rem);
  color: #ffffff;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  text-align: center;
  
  strong {
    color: #e1ad21;
  }
`;

const PricingTier = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background-color: rgba(255, 255, 255, 0.1);
  padding: 1.5rem;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 0 20px rgba(225, 173, 33, 0.5);
    transform: translateY(-5px);
  }

  h3 {
    margin-bottom: 0.5rem;
    color: #e1ad21;
    font-size: clamp(1.2rem, 2vw, 1.5rem);
  }

  .audience-label {
    font-family: "Helvetica";
    font-size: clamp(0.8rem, 1.1vw, 0.9rem);
    color: #cccccc;
    font-weight: normal;
    margin-bottom: 0.8rem;
    padding: 0;
    background: transparent;
    border-radius: 0;
    display: block;
    font-style: italic;
  }

  strong {
    font-size: clamp(1.2rem, 2.5vw, 1.5rem);
    color: #ffffff;
    margin-bottom: 0.5rem;
  }

  p {
    margin-bottom: 1rem;
    margin-top: 1rem;
    color: #ffffff;
    font-size: clamp(0.9rem, 1.5vw, 1rem);
  }

  ul {
    padding-left: 0;
    margin-top: 0.5rem;
    line-height: 1.1;
    color: #ffffff;
    list-style-type: none;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    font-size: clamp(0.9rem, 1.5vw, 1rem);
  }

  li {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    margin-bottom: 0.5rem;
    position: relative;
  }

  li span {
    display: inline-block;
    text-align: left;
    padding-left: 1.5rem;
    position: relative;
  }

  li::before {
    content: "✓";
    color: #e1ad21;
    position: absolute;
    left: calc(50% - 5rem);
  }

  @media (max-width: 768px) {
    text-align: center;

    ul {
      align-items: flex-start;
      width: fit-content;
      margin: 0 auto;
    }

    li {
      justify-content: flex-start;
    }

    li span {
      padding-left: 1.5rem;
    }

    li::before {
      left: 0;
    }
  }
`;

const RetainerTier = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background-color: ${props => props.isPopular ? 'rgba(225, 173, 33, 0.15)' : 'rgba(255, 255, 255, 0.1)'};
  padding: 1.5rem;
  border-radius: 8px;
  border: ${props => props.isPopular ? '2px solid #e1ad21' : '1px solid transparent'};
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    box-shadow: 0 0 20px rgba(225, 173, 33, 0.5);
    transform: translateY(-5px);
  }

  .popular-badge {
    position: absolute;
    top: -12px;
    left: 50%;
    transform: translateX(-50%);
    background-color: #e1ad21;
    color: #0a192f;
    padding: 0.3rem 0.8rem;
    border-radius: 20px;
    font-weight: bold;
    font-size: 0.8rem;
  }

  h3 {
    margin-bottom: 0.5rem;
    color: #e1ad21;
    font-size: clamp(1.2rem, 2vw, 1.5rem);
    ${props => props.isPopular ? 'margin-top: 1rem;' : ''}
  }

  .audience-label {
    font-size: clamp(0.8rem, 1.1vw, 0.9rem);
    color: #cccccc;
    font-weight: normal;
    margin-bottom: 0.8rem;
    padding: 0;
    background: transparent;
    border-radius: 0;
    display: block;
    font-style: italic;
  }

  strong {
    font-size: clamp(1.5rem, 3vw, 2rem);
    color: #ffffff;
    margin-bottom: 0.5rem;
  }

  .frequency {
    color: #cccccc;
    font-size: clamp(0.85rem, 1.2vw, 1rem);
    margin-bottom: 1rem;
  }

  p {
    margin-bottom: 1rem;
    margin-top: 1rem;
    color: #ffffff;
    font-size: clamp(0.9rem, 1.5vw, 1rem);
  }

  ul {
    padding-left: 0;
    margin-top: 0.5rem;
    line-height: 1.1;
    color: #ffffff;
    list-style-type: none;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    font-size: clamp(0.9rem, 1.5vw, 1rem);
  }

  li {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    margin-bottom: 0.5rem;
    position: relative;
  }

  li span {
    display: inline-block;
    text-align: left;
    padding-left: 1.5rem;
    position: relative;
  }

  li::before {
    content: "✓";
    color: #e1ad21;
    position: absolute;
    left: calc(50% - 7rem);
  }

  .dropdown-button {
    background: transparent;
    border: 1px solid rgba(225, 173, 33, 0.5);
    color: #e1ad21;
    padding: 0.8rem 1rem;
    border-radius: 6px;
    cursor: pointer;
    font-family: "Helvetica";
    font-size: clamp(0.85rem, 1.1vw, 0.95rem);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    transition: all 0.3s ease;
    margin-top: 0.5rem;

    &:hover {
      border-color: #e1ad21;
      background-color: rgba(225, 173, 33, 0.1);
    }

    .chevron {
      transition: transform 0.3s ease;
      transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
    }
  }

  .usage-content {
    background-color: rgba(225, 173, 33, 0.05);
    border: 1px solid rgba(225, 173, 33, 0.3);
    border-radius: 6px;
    padding: 1rem;
    margin-top: 0.5rem;
    font-size: clamp(0.85rem, 1.1vw, 0.95rem);
    color: #ffffff;

    .example {
      margin-bottom: 0.6rem;
      color: #cccccc;
    }

    strong {
      color: #e1ad21;
      font-size: clamp(0.9rem, 1.2vw, 1rem);
    }
  }

  .revision-policy {
    font-size: clamp(0.8rem, 1.1vw, 0.9rem);
    color: #cccccc;
    font-style: italic;
    margin-top: 0.5rem;
    padding-top: 0.5rem;
    border-top: 1px solid rgba(225, 173, 33, 0.3);
  }

  @media (max-width: 768px) {
    text-align: center;

    ul {
      align-items: flex-start;
      width: fit-content;
      margin: 0 auto;
    }

    li {
      justify-content: flex-start;
    }

    li span {
      padding-left: 1.5rem;
    }

    li::before {
      left: 0;
    }
  }
`;
const CalcJumpButton = styled.button`
  background: transparent;
  border: none;          /* no border */
  padding: 0;
  cursor: pointer;

  font-family: "Helvetica";
  font-size: clamp(0.9rem, 1.2vw, 1rem);
  color: rgba(225, 173, 33, 0.95);

  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  margin: 1rem auto 2rem auto;
  width: fit-content;

  opacity: 0.9;
  transition: opacity 0.2s ease, transform 0.2s ease;

  &:hover {
    opacity: 1;
    transform: translateY(1px);
    text-decoration: underline;
    text-underline-offset: 4px;
  }

  &:focus-visible {
    outline: 2px solid rgba(225, 173, 33, 0.6);
    outline-offset: 4px;
    border-radius: 6px;
  }
`;

const DownIcon = styled(ArrowDown)`
  opacity: 0.9;
`;
const SectionTitle = styled.h2`
  color: #e1ad21;
  font-family: "Raleway-Regular";
  margin-top: 2rem;
  font-size: clamp(1.5rem, 3vw, 2rem);
  text-align: center;
`;

const AddOnsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: clamp(0.5rem, 1vw, 1rem);
  margin-top: 3rem;
`;

const AddOnCard = styled(motion.div)`
  background-color: rgba(255, 255, 255, 0.1);
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 8px;
  font-family: "Helvetica";
  cursor: pointer;
  transition: background-color 0.3s ease;

  h4 {
    color: #e1ad21;
    margin-bottom: 0.5rem;
    font-size: clamp(0.95rem, 1.5vw, 1.1rem);
  }

  p {
    margin: 0.9rem 0;
    font-size: clamp(0.85rem, 1.2vw, 1rem);
  }

  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }

  @media (max-width: 768px) {
    text-align: center;
  }
`;

const BundlesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: clamp(0.5rem, 1vw, 1rem);
  margin-top: 3rem;
`;

const BundleCard = styled(motion.div)`
  background-color: rgba(255, 255, 255, 0.1);
  padding: 1rem;
  border-radius: 8px;
  font-family: Helvetica;
  cursor: pointer;
  transition: background-color 0.3s ease;

  h4 {
    color: #e1ad21;
    margin-bottom: 0.5rem;
    font-size: clamp(0.95rem, 1.5vw, 1.1rem);
  }

  p {
    margin: 0.9rem 0;
    font-size: clamp(0.85rem, 1.2vw, 1rem);
  }

  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }

  @media (max-width: 768px) {
    text-align: center;
  }
`;

const Disclaimer = styled.p`
  font-family: "Helvetica";
  font-style: italic;
  margin-bottom: -2.5rem;
  margin-top: 4rem;
  text-align: center;
  font-size: clamp(0.8rem, 1.2vw, 0.9rem);
  color: #cccccc;

  @media (max-width: 768px) {
    margin-bottom: 0rem;
  }
`;

const CustomQuoteButton = styled.button`
  background-color: #e1ad21;
  font-family: "Helvetica";
  color: #0a192f;
  border: none;
  padding: 1rem 2rem;
  font-size: 1rem;
  font-weight: bold;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 2rem;
  margin-bottom: 2rem;
  display: block;
  margin-left: auto;
  margin-right: auto;

  &:hover {
    background-color: #ffffff;
  }
`;

function Pricing() {
  const [activeTab, setActiveTab] = useState('vr');
  const [openRetainerDropdown, setOpenRetainerDropdown] = useState(null);
  const navigate = useNavigate();
  const calculatorRef = useRef(null);

const scrollToCalculator = () => {
  calculatorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

  const toggleRetainerDropdown = (index) => {
    setOpenRetainerDropdown(openRetainerDropdown === index ? null : index);
  };

  const PRICING_TIERS = {
    rendering: [
      {
        title: "Basic Rendering",
        priceRange: "€250 - €1,000",
        price: "From €300",
        description: "For spaces up to 50 sqm",
        audience: "Perfect for homeowners and designers exploring concepts",
        sqmNote: "€6/sqm base",
        features: [
          "Unfurnished",
          "90 Second Video",
          "1080p Resolution",
          "5 Stills Included",
          "7-day Delivery",
          "2 Revisions",
                ],
        revisionPolicy: "2 rounds included, additional revisions at €75 - €125 per revision"
      },
      {
        title: "Standard Rendering",
        priceRange: "€1,000 - €3,500",
        price: "From €700",
        description: "For spaces 50-100 sqm",
        audience: "Great for interior designers and real estate agents",
        sqmNote: "€8/sqm base",
        features: [
          "Furnished",
          "2 Minute Video",
          "1080p Resolution",
          "10 Stills Included",
          "5-day Delivery",
          "2 Revisions",
          
          
        ],
        revisionPolicy: "2 rounds included, additional revisions at €75 - €125 per revision"
      },
      {
        title: "Premium Rendering",
        priceRange: "€3,000 - €6,000",
        price: "From €3,600",
        description: "For spaces 100-150 sqm",
        audience: "Great for architects and large development projects",
        sqmNote: "€10/sqm base",
        features: [
          "Furnished",
          "2 Minute Video",
          "15 Stills Included",
          "Up to 4K Resolution",
          "5-day Delivery",
          "2 Revisions",
         
        ],
        revisionPolicy: "2 rounds included, additional revisions at €75 - €125 per revision"
      }
    ],
    vr: [
      {
        title: "Basic VR Experience",
        priceRange: "€300 - €1,250",
        price: "From €400",
        description: "For spaces up to 50 sqm",
        audience: "Perfect for on-plan apartment tours",
        sqmNote: "€8/sqm base",
        features: [
          "Unfurnished",
          "No Customization",
          "Standard VR Tour",
          "7-12 day Delivery",
          "2 Revisions",
          
         
        ],
        revisionPolicy: "2 rounds included, additional revisions at €75 - €125 per revision"
      },
      {
        title: "Standard VR Experience",
        priceRange: "€1,550 - €6,000",
        price: "From €1,550",
        description: "For spaces 50-100 sqm",
        audience: "Great for interior designers pitching design options",
        sqmNote: "€10/sqm base",
        features: [
          "Furnished",
          "Customizable",
          "7-12 day Delivery",
          "2 Revisions",
          
          
        ],
        revisionPolicy: "2 rounds included, additional revisions at €75 - €125 per revision"
      },
      {
        title: "Premium VR Experience",
        priceRange: "€4,550 - €7,050",
        price: "From €4,550",
        description: "For spaces 100-150 sqm",
        audience: "Great for high-end developers with branded campaigns",
        sqmNote: "€12/sqm base",
        features: [
          "Furnished",
          "Customizable",
          "Interior Deisgn",
          "7-12 day Delivery",
          "2 Revisions",
        ],
        revisionPolicy: "2 rounds included, additional revisions at €75 - €125 per revision"
      }
    ]
  };

  const RETAINER_TIERS = [
  {
    title: "Collaborative Partner",
    price: "€2,500",
    description1: "/Month",
    description: "€3,750 as monthly credits",
    audience: "Perfect for freelancers and small design studios",
    isPopular: false,
    features: [
      "Up to 250 sqm",
      "15% discount applied",
      "Monthly planning call",
      "Up to 4 Projects per month", 
      "7-12 day Delivery",
      "Email support",
      "2 revisions",
    ],
    revisionPolicy: "Work beyond €3,750 billed at standard rates. Credits reset monthly.",
    usageExamples: [
      "2x 40sqm Furnished Rendering = €1,184 value (2 projects)",
      "3x 30sqm Unfurnished Rendering = €810 value (3 projects)", 
      "1x 75sqm Unfurnished Rendering = €1,125 value (1 project)"
    ]
  },
  {
    title: "Strategic Partner",
    price: "€4,000",
    description1: "/Month",
    description: "€5,500 as monthly credits",
    audience: "Ideal for interior design studios and real estate agencies",
    isPopular: true,
    badge: "★ MOST POPULAR ★",
    features: [
      "Up to 450sqm",
      "15% discount applied",
      "Direct communication line",
      "Up to 5 Projects per month",
      "Monthly planning call", 
      "5-7 day Priority",
      "2 revisions",
    ],
    revisionPolicy: "Work beyond €5,500 billed at standard rates. Credits reset monthly.",
    
  },
  {
    title: "Executive Partner",
    price: "€6,250",
    description1: "/Month",
    description: "€8,500 as monthly credits",
    audience: "Built for enterprises and high-volume development firms",
    isPopular: false,
    features: [
      "Up to 550sqm",
      "15% discount applied",
      "Weekly strategic check-in",
      "Up to 7 Projects per month",
      "Priority queue access",
      "5-7 day Priority",
      "3 revisions",
    ],
    revisionPolicy: "Work beyond €8,500 billed at standard rates. Credits reset monthly.",
  }
];

  const ADD_ONS = [
    { name: "4K Resolution Upgrade", price: "€500", description: "Add 4K quality to any rendering" },
    { name: "Additional Stills Package", price: "€150-200", description: "Per Still (Discounted if bundled)" },   
    { name: "Additional Revisions", price: "€75 - €125", description: "Per revision round beyond included rounds" }, 
    { name: "Interior Design & Furnishing", price: "€800 - €2,150", description: "Complete interior design consultation and staging" },
    { name: "Complexity Surcharge", price: "15-35%", description: "Applied for intricate architectural details or special requirements" },
  
  ];

  const BUNDLES = [
    { name: "1080p Rendering + VR", discount: "18% off combined", description: "Pair any rendering with basic VR experience" },
    { name: "4K Rendering + VR", discount: "22% off combined", description: "Pair any rendering with premium VR" },
    { name: "Full Creative Package", discount: "28% off combined", description: "Rendering + VR + Interior Design" }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const handleCustomQuoteClick = () => {
    const message = "I'm interested in a custom quote for a large space (150+ sqm) or special project.";
    navigate('/contact', { state: { prefilledMessage: message } });
  };

  return (
    <PricingContainer>
      <Title>Our Pricing</Title>
      
      <Description>
        Transform your vision into reality. Harness the power of hyper-realistic renderings and immersive VR experiences that don't just showcase your apartments – they transport your audience into them.
      </Description>
      
      <TabContainer>
        <TabButton 
          active={activeTab === 'rendering'}
          onClick={() => setActiveTab('rendering')}
        >
          Rendering Services
        </TabButton>
        <TabButton 
          active={activeTab === 'vr'}
          onClick={() => setActiveTab('vr')}
        >
          VR Experience
        </TabButton>
        <TabButton 
          active={activeTab === 'retainer'}
          onClick={() => setActiveTab('retainer')}
        >
          Creative Partnership
        </TabButton>
      </TabContainer>

      <CalcJumpButton onClick={scrollToCalculator}>
      Jump to pricing calculator <DownIcon size={18} />
      </CalcJumpButton>
      

      {activeTab === 'retainer' ? (
        <>
          <SectionTitle style={{ marginTop: 0 }}>Creative Partnership</SectionTitle>
          
          <CapacityNote>
            We carefully limit active retainers to maintain our quality standards and guaranteed <strong>4-7 day turnarounds.</strong>
          </CapacityNote>

          <RetainerGrid
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {RETAINER_TIERS.map((tier, index) => (
              <RetainerTier
                key={index}
                isPopular={tier.isPopular}
                isOpen={openRetainerDropdown === index}
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {tier.isPopular && <div className="popular-badge">{tier.badge}</div>}
                <h3>{tier.title}</h3>
                {tier.audience && <div className="audience-label">{tier.audience}</div>}
                <p className="frequency">
  <strong style={{ marginRight: '0.3rem' }}>{tier.price}</strong>
  {tier.description1}
</p>
                <p style={{ fontSize: 'clamp(0.85rem, 1.2vw, 0.95rem)', color: '#e1ad21' }}>{tier.description}</p>
                <ul>
                  {tier.features.map((feature, featureIndex) => (
                    <li key={featureIndex}><span>{feature}</span></li>
                  ))}
                </ul>
                
                
                <div className="revision-policy">{tier.revisionPolicy}</div>
              </RetainerTier>
            ))}
          </RetainerGrid>

          <p style={{ textAlign: 'center', color: '#cccccc', fontSize: 'clamp(0.85rem, 1.2vw, 1rem)', marginTop: '2rem' }}>
            <strong>Flexible Project Mix:</strong> Combine any rendering or VR projects (different sqm sizes) to fit within your monthly credits. No project type restrictions.
          </p>
          <p style={{ textAlign: 'center', color: '#cccccc', fontSize: 'clamp(0.85rem, 1.2vw, 1rem)', marginTop: '1rem' }}>
            Unused credits expire monthly. We're happy to discuss carryover for genuine scheduling conflicts, just reach out!
          </p>
          <p style={{ textAlign: 'center', color: '#e1ad21', fontSize: 'clamp(0.85rem, 1.2vw, 1rem)', marginTop: '1rem', fontWeight: 'bold' }}>
            All retainer pricing includes a <strong>15% discount</strong> on our standard rates.
          </p>
        </>
      ) : (
        <PricingGrid
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          key={activeTab}
        >
          {PRICING_TIERS[activeTab].map((tier, index) => (
            <PricingTier
              key={index}
              variants={itemVariants}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <h3>{tier.title}</h3>
              {tier.audience && <div className="audience-label">{tier.audience}</div>}
              <strong>{tier.price}</strong>
              <p style={{ fontSize: 'clamp(0.85rem, 1.2vw, 0.95rem)', color: '#e1ad21' }}>{tier.sqmNote}</p>
              <p>{tier.description}</p>

              <ul>
                {tier.features.map((feature, featureIndex) => (
                  <li key={featureIndex}><span>{feature}</span></li>
                ))}
              </ul>

              {tier.addOns && (
                <div style={{ marginTop: '0.5rem' }}>
                  {tier.addOns.map((addon, aIdx) => (
                    <p key={aIdx} style={{ fontSize: '0.8rem', margin: '0.2rem 0', color: '#cccccc' }}>
                      • {addon.name}: <strong style={{ color: '#e1ad21' }}>{addon.price}</strong>
                    </p>
                  ))}
                </div>
              )}

              <p style={{ fontSize: '0.75rem', fontStyle: 'italic', marginTop: '0.8rem' }}>{tier.revisionPolicy}</p>
            </PricingTier>
          ))}
        </PricingGrid>
      )}

      <CustomQuoteButton onClick={handleCustomQuoteClick}>
        Get Custom Quote for Large Spaces (150+ sqm)
      </CustomQuoteButton>

      <SectionTitle>Add-ons & Services</SectionTitle>
      <AddOnsGrid>
        {ADD_ONS.map((addon, index) => (
          <AddOnCard
            key={index}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <h4>{addon.name}</h4>
            <p style={{ color: '#e1ad21', fontWeight: 'bold' }}>{addon.price}</p>
            <p>{addon.description}</p>
          </AddOnCard>
        ))}
      </AddOnsGrid>

      {/* <SectionTitle>Bundle Discounts</SectionTitle>
      <BundlesGrid>
        {BUNDLES.map((bundle, index) => (
          <BundleCard
            key={index}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <h4>{bundle.name}</h4>
            <p style={{ color: '#e1ad21', fontWeight: 'bold', fontSize: '1.1rem' }}>{bundle.discount}</p>
            <p>{bundle.description}</p>
          </BundleCard>
        ))}
      </BundlesGrid> */}

      <SectionTitle ref={calculatorRef}>Pricing Calculator</SectionTitle>
<InteractivePricingCalculator />
      <p style={{ textAlign: 'center', color: '#cccccc', marginBottom: '2rem', fontSize: 'clamp(0.85rem, 1.2vw, 1rem)' }}>
        Use our interactive tool to customize your package and see real-time pricing updates based on sqm and options.
      </p>

      <Disclaimer>
        * For spaces over 150 sqm, commercial projects, or special requirements, please contact us for a custom quote. Complexity surcharge (15-35%) may apply for intricate architectural details.
      </Disclaimer>
    </PricingContainer>
  );
}

export default Pricing;
